import { WebSdk } from '@effect/opentelemetry'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import type { SpanProcessor } from '@opentelemetry/sdk-trace-base'
import {
  BatchSpanProcessor,
  ConsoleSpanExporter,
} from '@opentelemetry/sdk-trace-web'
import { Effect, Layer } from 'effect'

import { PublicApiConfig, PublicOtelConfig } from '@viziertronic/config/public'

export interface BrowserTracingConfig {
  serviceName: string
  endpoint: string
  debug?: boolean
}

/**
 * Creates an Effect Layer for browser tracing using @effect/opentelemetry WebSdk.
 * This integrates Effect's tracer with OpenTelemetry, ensuring trace context
 * is properly propagated in RPC calls.
 */
function createBrowserTracingLayer(config: BrowserTracingConfig) {
  const spanProcessors: SpanProcessor[] = []

  // Add OTLP exporter if endpoint is configured
  if (config.endpoint) {
    spanProcessors.push(
      new BatchSpanProcessor(
        new OTLPTraceExporter({
          url: `${config.endpoint}/v1/traces`,
        }),
      ),
    )
  }

  // Add console exporter for debugging
  if (config.debug) {
    spanProcessors.push(new BatchSpanProcessor(new ConsoleSpanExporter()))
  }

  return WebSdk.layer(() => ({
    resource: {
      serviceName: config.serviceName,
    },
    spanProcessor: spanProcessors,
  }))
}

/**
 * Creates a browser tracing layer from public config.
 * Uses API endpoint + /otel as the trace endpoint.
 * Returns Resource.layerEmpty if not in browser runtime.
 */
export const BrowserTracingLayer = Effect.gen(function* () {
  const { apiEndpoint } = yield* PublicApiConfig
  const { serviceName } = yield* PublicOtelConfig

  if (!globalThis.window) {
    yield* Effect.logDebug('Browser tracing skipped: not in browser runtime')
    return Layer.empty
  }

  const endpoint = `${apiEndpoint}/otel`

  yield* Effect.logInfo('Creating browser tracing layer').pipe(
    Effect.annotateLogs({ endpoint, serviceName }),
  )

  return createBrowserTracingLayer({ serviceName, endpoint })
}).pipe(Layer.unwrap)
